import BookmarkIcon from '@mui/icons-material/Bookmark';
import CalculateIcon from '@mui/icons-material/Calculate';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ComputerIcon from '@mui/icons-material/Computer';
import CopyrightIcon from '@mui/icons-material/Copyright';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import EditNoteIcon from '@mui/icons-material/EditNote';
import GitHubIcon from '@mui/icons-material/GitHub';
import XIcon from '@mui/icons-material/X';

import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { isMultipool } from '../abelcommon';
import { ConditionalTooltip } from '../abelcommon/components/ConditionalTooltip';
import { LoadingOverlay } from '../abelcommon/components/LoadingOverlay';
import { RequireSignInLevel, useElevationSignInCheck } from '../abelcommon/hooks/useElevationSignInCheck';
import { useLoginMiningAccount } from '../abelcommon/hooks/useLoginMiningAccount';
import { globalAlertState, selectedMiningAccountState, switchAccountMenuOpenState, useUserSignedIn } from '../api';
import { MiningAccount, loadBalancerAPI } from '../api/load-balancer';
import { useLayoutState } from '../components/BarDrawerLayout';
import { DarkLightModeSwitch } from '../components/DarkLightModeSwitch';
import { UserLoginMenu } from '../components/UserLoginMenu';
import { environment as env } from '../environments/environment';
import SignUpInOut from './SignUpInOut';
import { LevelOfUser, useLevelUserCheck } from '../abelcommon/hooks/useLevelUserCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { useTranslation } from 'react-i18next';

interface Route {
  id: string;
  icon?: React.ReactNode;
  to?: string;
  requireSignIn?: boolean;
  requireSignInLevel?: RequireSignInLevel;
  levelOfUser?: LevelOfUser;
}

type RouteWithChildren = Route & { children?: Route[] };

function isExternalURL(url: string) {
  return url.startsWith('http://') || url.startsWith('https://');
}

export const NavLink = ({
  route,
  jump,
  onDisabledClick,
}: {
  route: Route;
  jump: (to: string) => void;
  onDisabledClick?: (level: RequireSignInLevel) => void;
}) => {
  const location = useLocation();
  const { t } = useTranslation();

  const { status: signedInStatus, highestLevel } = useElevationSignInCheck(
    route.requireSignInLevel ?? RequireSignInLevel.User
  );

  const signInStatusLevelMessageMap = {
    [RequireSignInLevel.User]: t('navigatorPage.pleaseSignInToAccess'),
    [RequireSignInLevel.MiningAccount]: t('navigatorPage.pleaseSelectAMiningAccountToAccess'),
  };

  const isDisabled = route.requireSignIn && !signedInStatus;
  const disableTooltip = signInStatusLevelMessageMap[highestLevel];

  return (
    <>
      <ConditionalTooltip
        shouldShow={isDisabled}
        title={disableTooltip}
        placement="top"
        arrow
        enterTouchDelay={0}
        onClick={() =>
          isDisabled && highestLevel === RequireSignInLevel.MiningAccount && onDisabledClick?.(highestLevel)
        }
      >
        <span>
          <ListItemButton
            selected={route.to === location.pathname}
            sx={{ py: 0.5, px: 2, color: 'text.secondary' }}
            onClick={() => jump(route.to)}
            disabled={isDisabled}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>{route.icon}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>{route.id}</ListItemText>
            {isExternalURL(route.to) && <OpenInNewIcon sx={{ width: 14, height: 14 }} />}
          </ListItemButton>
        </span>
      </ConditionalTooltip>
    </>
  );
};

export function Navigator() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [layoutState, setLayoutState] = useLayoutState();
  const { isSignedIn: isUserSignedIn } = useUserSignedIn();

  const [isOpenSwitchAccount, setIsOpenSwitchAccount] = useRecoilState(switchAccountMenuOpenState);
  const [selectedMiningAccountValue, setSelectedMiningAccount] = useRecoilState(selectedMiningAccountState);
  const [, setGlobalAlertState] = useRecoilState(globalAlertState);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const { highestLevel } = useLevelUserCheck(LevelOfUser.Admin || LevelOfUser.ContentManagement);
  const { t } = useTranslation();
  const categories: RouteWithChildren[] = [
    {
      id: t('navigatorPage.admin'),
      children: [
        {
          id: t('navigatorPage.analytics'),
          icon: <AnalyticsRoundedIcon />,
          to: '/analytics',
          requireSignIn: true,
          levelOfUser: LevelOfUser.Admin,
        },
      ],
    },
    {
      id: t('navigatorPage.content'),
      children: [
        {
          id: t('navigatorPage.contentManagement'),
          icon: <EditNoteIcon />,
          to: '/content-management',
          requireSignIn: true,
          levelOfUser: LevelOfUser.ContentManagement,
        },
      ],
    },
    {
      id: t('navigatorPage.home'),
      icon: <HomeRoundedIcon />,
      to: '/home',
      requireSignIn: false,
      children: [
        {
          id: t('navigatorPage.quickstartGuide'),
          icon: <BookmarkIcon />,
          to: '/home/guide',
          requireSignIn: false,
        },
        {
          id: t('navigatorPage.hashRateCalculator'),
          icon: <CalculateIcon />,
          to: '/home/hash-rate-calculator',
          requireSignIn: false,
        },
        {
          id: t('navigatorPage.minedList'),
          icon: <ChecklistIcon />,
          to: '/home/mined-list',
          requireSignIn: false,
        },
      ],
    },
    {
      id: t('navigatorPage.account'),
      children: [
        {
          id: t('navigatorPage.settings'),
          icon: <ManageAccountsIcon />,
          to: '/account/settings',
          requireSignIn: true,
          requireSignInLevel: RequireSignInLevel.User,
        },
        {
          id: t('navigatorPage.dashboard'),
          icon: <DashboardIcon />,
          to: '/account/dashboard',
          requireSignIn: true,
          requireSignInLevel: RequireSignInLevel.MiningAccount,
        },
        {
          id: t('navigatorPage.machines'),
          icon: <ComputerIcon />,
          to: '/account/machine',
          requireSignIn: true,
          requireSignInLevel: RequireSignInLevel.MiningAccount,
        },
      ],
    },
    {
      id: t('navigatorPage.links'),
      children: [
        { id: t('navigatorPage.officialWebsite'), icon: <CopyrightIcon />, to: 'https://www.pqabelian.io' },
        { id: t('navigatorPage.blockchainExplorer'), icon: <PublicIcon />, to: 'https://explorer.pqabelian.io' },
        { id: `Abelian's X`, icon: <XIcon />, to: 'https://x.com/PQabelian' },
        {
          id: t('navigatorPage.discord'),
          icon: <FontAwesomeIcon icon={faDiscord} />,
          to: 'https://discord.gg/5rrDxP29hx',
        },
        { id: t('navigatorPage.github'), icon: <GitHubIcon />, to: 'https://github.com/pqabelian' },
      ],
    },
  ];

  const {
    data: miningAccounts,
    isLoading: isLoadingMiningAccounts,
    error: miningAccountsError,
  } = loadBalancerAPI.hookWrapperQuery.useGetMiningAccounts(!isUserSignedIn);

  const closeMobileDrawer = useCallback(() => {
    if (!layoutState.isDesktop) {
      setLayoutState({ ...layoutState, mobileDrawerOpen: false });
    }
  }, []);

  const jump = (to: string) => {
    if (isExternalURL(to)) {
      window.open(to, '_blank');
    } else {
      navigate(to);
    }
    setTimeout(closeMobileDrawer, 600);
  };

  const loginMiningAccount = useLoginMiningAccount(closeMobileDrawer);

  const handleSelectMiningAccount = async (miningAccount: MiningAccount) => {
    if (!miningAccount.username || !miningAccount.password || miningAccount.id === selectedMiningAccountValue?.id) {
      return;
    }

    setIsLoginLoading(true);
    const { error } = await loginMiningAccount(miningAccount.username, miningAccount.password);
    setIsLoginLoading(false);
    if (error) {
      setGlobalAlertState((prevState) => ({
        ...prevState,
        isOpen: true,
        severity: 'error',
        message: t('navigatorPage.errorMessageLoginMiningAccount'),
      }));
    } else {
      setSelectedMiningAccount(miningAccount);
    }
  };

  useEffect(() => {
    if (
      !(
        miningAccounts &&
        miningAccounts.length > 0 &&
        (!selectedMiningAccountValue || miningAccounts.every(({ id }) => id !== selectedMiningAccountValue.id))
      )
    )
      return;

    const latestCreatedMiningAccount = miningAccounts.find(({ username, password }) => username && password);
    if (!latestCreatedMiningAccount) return;

    setIsLoginLoading(true);
    loginMiningAccount(latestCreatedMiningAccount.username, latestCreatedMiningAccount.password).then(({ error }) => {
      setIsLoginLoading(false);
      if (error) {
        setGlobalAlertState((prevState) => ({
          ...prevState,
          isOpen: true,
          severity: 'error',
          message: `${t('navigatorPage.failToAutoLoginWithAccount')} ${latestCreatedMiningAccount.name}. ${t('navigatorPage.pleaseTryToSelectAccountManually')}.`,
        }));
      } else {
        setSelectedMiningAccount(latestCreatedMiningAccount);
      }
    });
  }, [
    loginMiningAccount,
    miningAccounts,
    selectedMiningAccountValue,
    setGlobalAlertState,
    setSelectedMiningAccount,
    t,
  ]);

  const filteredCategories = categories.filter((category) => {
    if (!category.children.some((child) => child.levelOfUser)) {
      return true;
    }
    return category.children.some((child) => highestLevel >= child.levelOfUser);
  });

  return (
    <Stack sx={{ minHeight: { xs: '-webkit-fill-available', sm: '100vh', overflowX: 'hidden' } }}>
      <List
        disablePadding
        sx={{
          mb: layoutState.isDesktop ? 4 : 2,
          '& .Mui-selected': { color: 'appBlue.lightest', backgroundColor: 'appGlass.main' },
        }}
      >
        <ListItem sx={{ height: layoutState.isDesktop ? 60 : 48, px: 0.5, justifyContent: 'space-between' }}>
          <DarkLightModeSwitch sx={{ mr: 2 }} />
          <Stack sx={{ flexDirection: 'row', justifyContent: 'right', alignItems: 'center' }}>
            {isMultipool() ? (
              <UserLoginMenu onNavigate={!layoutState.isDesktop ? closeMobileDrawer : undefined} />
            ) : (
              <SignUpInOut />
            )}
            {!layoutState.isDesktop && (
              <IconButton onClick={closeMobileDrawer} sx={{ edge: 'end', color: 'inherit', borderRadius: 0 }}>
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Stack>
        </ListItem>
        <Divider />
        {filteredCategories.map(({ id, children, to, icon, requireSignIn, requireSignInLevel }) => {
          const shouldShowAccountSelectMenu = id === t('navigatorPage.account') && isUserSignedIn;

          return (
            <Box key={id} sx={theme.palette.mode === 'dark' ? { backgroundColor: 'appBlue.main' } : {}}>
              {to ? (
                <NavLink route={{ id, icon, to, requireSignIn, requireSignInLevel }} jump={jump} />
              ) : (
                <ListItem
                  sx={{
                    py: layoutState.isDesktop ? 1 : 0.5,
                    px: 2,
                    ...(shouldShowAccountSelectMenu ? { display: 'block' } : {}),
                  }}
                >
                  <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
                    <ListItemText sx={{ color: 'appGray.contrastText' }}>{id}</ListItemText>
                    {shouldShowAccountSelectMenu && (
                      <IconButton size="small" onClick={() => setIsOpenSwitchAccount((prevState) => !prevState)}>
                        {isOpenSwitchAccount ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    )}
                  </Stack>
                  {shouldShowAccountSelectMenu && (
                    <Collapse in={isOpenSwitchAccount} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1 }}>
                        {isLoadingMiningAccounts ? (
                          <CircularProgress sx={{ mx: 'auto', display: 'block' }} size={24} />
                        ) : !miningAccounts || miningAccountsError ? (
                          <Alert severity="error" sx={{ backgroundColor: 'transparent', p: 0 }}>
                            {t('navigatorPage.cannotGetMiningAccountInfo')}
                          </Alert>
                        ) : miningAccounts.length === 0 ? (
                          <Typography variant="body2">{t('navigatorPage.noMiningAccountFound')}</Typography>
                        ) : (
                          <List sx={{ py: 0 }}>
                            <RadioGroup value={selectedMiningAccountValue?.id ?? null}>
                              {miningAccounts.map((miningAccount) => (
                                <ConditionalTooltip
                                  shouldShow={!miningAccount.username || !miningAccount.password}
                                  title={t('navigatorPage.thisAccountCantBeSelected')}
                                  placement="top"
                                  arrow
                                  enterTouchDelay={0}
                                  key={miningAccount.id}
                                >
                                  <ListItem disablePadding>
                                    <ListItemButton
                                      role={undefined}
                                      onClick={() => handleSelectMiningAccount(miningAccount)}
                                      dense
                                      sx={{ px: 0 }}
                                      disabled={!miningAccount.username || !miningAccount.password}
                                    >
                                      <ListItemIcon>
                                        <Radio value={miningAccount.id} size="small" />
                                      </ListItemIcon>
                                      <ListItemText id={miningAccount.id} primary={miningAccount.name} />
                                    </ListItemButton>
                                  </ListItem>
                                </ConditionalTooltip>
                              ))}
                            </RadioGroup>
                          </List>
                        )}
                      </Box>
                    </Collapse>
                  )}
                </ListItem>
              )}
              {children?.map(({ id: childId, icon, to, requireSignIn = false, requireSignInLevel }: any) => (
                <NavLink
                  key={childId}
                  route={{ id: childId, icon, to, requireSignIn, requireSignInLevel }}
                  jump={jump}
                  onDisabledClick={(level) => {
                    if (level === RequireSignInLevel.MiningAccount) {
                      setGlobalAlertState((prevState) => ({
                        ...prevState,
                        isOpen: true,
                        severity: 'warning',
                        autoHideDuration: null,
                        message: t('navigatorPage.cantAccessThisPage'),
                      }));
                    }
                    setIsOpenSwitchAccount(true);
                  }}
                />
              ))}
              <Divider />
              {isLoginLoading && <LoadingOverlay sx={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }} />}
            </Box>
          );
        })}
      </List>
      <Stack sx={{ mt: 'auto', mb: 1, width: 1, alignItems: 'center' }}>
        <Typography
          variant={'body2'}
          sx={{ fontWeight: 500, textAlign: 'center' }}
        >{`Maxpool © ${new Date().getFullYear()}`}</Typography>
        <Typography variant={'body2'} sx={{ fontWeight: 500 }}>
          {t('navigatorPage.poweredBy')}{' '}
          <Link
            href={'https://abelian.info'}
            target={'_blank'}
            sx={{ color: 'appBlue.contrastText', textDecoration: 'none' }}
          >
            {t('navigatorPage.abelianFoundation')}
          </Link>
        </Typography>
      </Stack>
    </Stack>
  );
}

export default Navigator;
